import React, { useEffect, useState, useMemo } from "react";
import {
  ExclamationCircleIcon,
  InboxIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarDaysIcon,
  SunIcon,
  MinusCircleIcon,
} from "@heroicons/react/24/outline";
import MonthYearPicker from "./MonthYearPicker";

const LATE_CHECKIN_TIME = "10:40:00";
const TABLE_HEIGHT = "max-h-[460px]";

const formatTime = (t) => {
  if (!t) return "—";
  const [hours, minutes] = t.split(":");
  const h = parseInt(hours, 10);
  return `${h % 12 || 12}:${minutes} ${h >= 12 ? "PM" : "AM"}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const getDayName = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short" });
};

const calculateDuration = (start, end) => {
  if (!start || !end) return "—";
  const base = new Date().toISOString().split("T")[0];
  const diff = new Date(`${base}T${end}`) - new Date(`${base}T${start}`);
  if (diff <= 0) return "—";
  return `${Math.floor(diff / 3600000)}h ${Math.floor((diff % 3600000) / 60000)}m`;
};

const getLateInfo = (checkIn) => {
  if (!checkIn) return null;
  const base = new Date(`1970-01-01T${LATE_CHECKIN_TIME}`);
  const cin  = new Date(`1970-01-01T${checkIn}`);
  if (cin <= base) return null;
  const diff = cin - base;
  return `+${String(Math.floor(diff / 3600000)).padStart(2, "0")}:${String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0")} late`;
};

/* ── Status config ─────────────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  Present:     { icon: CheckCircleIcon,  rowBg: "",                      badge: "bg-emerald-50 text-emerald-700 border border-emerald-200", dot: "bg-emerald-500" },
  Absent:      { icon: XCircleIcon,      rowBg: "bg-rose-50/50",         badge: "bg-rose-50 text-rose-700 border border-rose-200",          dot: "bg-rose-500" },
  Leave:       { icon: CalendarDaysIcon, rowBg: "bg-amber-50/50",        badge: "bg-amber-50 text-amber-700 border border-amber-200",       dot: "bg-amber-500" },
  "Half Day":  { icon: MinusCircleIcon,  rowBg: "bg-indigo-50/40",       badge: "bg-indigo-50 text-indigo-700 border border-indigo-200",    dot: "bg-indigo-500" },
  Holiday:     { icon: SunIcon,          rowBg: "bg-violet-50/40",       badge: "bg-violet-50 text-violet-700 border border-violet-200",    dot: "bg-violet-500" },
  "Week Off":  { icon: MinusCircleIcon,  rowBg: "",                      badge: "bg-slate-100 text-slate-600 border border-slate-200",      dot: "bg-slate-400" },
  "No Record": { icon: MinusCircleIcon,  rowBg: "",                      badge: "bg-slate-50 text-slate-400 border border-slate-100",       dot: "bg-slate-300" },
};

const getCfg = (s) => STATUS_CONFIG[s] ?? STATUS_CONFIG["No Record"];

/* ── Summary Bar ─────────────────────────────────────────────────────────── */
const SummaryBar = ({ data }) => {
  const counts = { Present: 0, Absent: 0, Leave: 0, "Half Day": 0, Holiday: 0, "Week Off": 0 };
  data.forEach((r) => { if (counts[r.status] !== undefined) counts[r.status]++; });

  const colorMap = {
    Present:    "bg-emerald-50 text-emerald-700 border border-emerald-200",
    Absent:     "bg-rose-50 text-rose-700 border border-rose-200",
    Leave:      "bg-amber-50 text-amber-700 border border-amber-200",
    "Half Day": "bg-indigo-50 text-indigo-700 border border-indigo-200",
    Holiday:    "bg-violet-50 text-violet-700 border border-violet-200",
    "Week Off": "bg-slate-100 text-slate-600 border border-slate-200",
  };

  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(counts).map(([key, val]) => (
        <div
          key={key}
          className={`flex items-center gap-1.5 px-3 py-1 rounded-xl text-[10px] font-bold uppercase tracking-wider border ${colorMap[key]}`}
        >
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getCfg(key).dot}`} />
          {key} <span className="font-extrabold ml-0.5">{val}</span>
        </div>
      ))}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════════ */
const AttendanceTable = ({ data = [], isLoading = false, error = null, fetchAttendance }) => {
  const now = new Date();
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const [selYear, setSelYear]   = useState(now.getFullYear());

  useEffect(() => {
    const handler = () => { if (typeof fetchAttendance === "function") fetchAttendance(); };
    window.addEventListener("attendance-updated", handler);
    return () => window.removeEventListener("attendance-updated", handler);
  }, [fetchAttendance]);

  const filteredData = useMemo(() => {
    if (!selMonth || !selYear) return data;
    return data.filter((r) => {
      if (!r.attendance_date) return false;
      const d = new Date(r.attendance_date + "T00:00:00");
      return d.getMonth() + 1 === selMonth && d.getFullYear() === selYear;
    });
  }, [data, selMonth, selYear]);

  /* ── Loading ── */
  if (isLoading) return (
    <div className="flex flex-col items-center justify-center py-14 gap-3">
      <div className="w-7 h-7 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin" />
      <span className="text-sm font-medium text-slate-500">Loading attendance records…</span>
    </div>
  );

  /* ── Error ── */
  if (error) return (
    <div className="flex flex-col items-center justify-center py-14 gap-2">
      <ExclamationCircleIcon className="w-8 h-8 text-rose-500" />
      <span className="text-sm font-medium text-rose-600">{error}</span>
    </div>
  );

  return (
    <>
      <style>{`
        .at-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
        .at-scroll::-webkit-scrollbar-track { background: transparent; }
        .at-scroll::-webkit-scrollbar-thumb { background: #c7d2fe; border-radius: 99px; }
      `}</style>

      <div className="flex flex-col gap-4">
        {/* Controls row */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:justify-between">
          <MonthYearPicker
            selectedMonth={selMonth}
            selectedYear={selYear}
            onChange={({ month, year }) => {
              setSelMonth(month ?? now.getMonth() + 1);
              setSelYear(year  ?? now.getFullYear());
            }}
            label="Select Month"
          />
          <div className="flex-1 min-w-0">
            <SummaryBar data={filteredData} />
          </div>
        </div>

        {/* Empty state */}
        {filteredData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 gap-2 rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/30">
            <InboxIcon className="w-9 h-9 text-indigo-300" />
            <span className="text-sm font-medium text-slate-500">No records for this month.</span>
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden border border-indigo-100/60 shadow-sm shadow-indigo-100/40">
            <div className={`overflow-y-auto overflow-x-auto at-scroll ${TABLE_HEIGHT}`}>
              <table className="w-full text-sm text-left" style={{ minWidth: "700px" }}>
                <thead className="sticky top-0 z-10" style={{ background: "#f8faff" }}>
                  <tr>
                    {["Date", "Check In", "In Location", "Check Out", "Out Location", "Hours", "Status", "Remarks"].map((h) => (
                      <th key={h} className="py-3 px-3 whitespace-nowrap border-b border-indigo-100/60">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{h}</span>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {filteredData.map((record, idx) => {
                    const cfg      = getCfg(record.status);
                    const Icon     = cfg.icon;
                    const lateInfo = getLateInfo(record.check_in);
                    let remarks    = null;

                    if (record.holiday_name) {
                      remarks = <span className="text-violet-700 font-medium text-xs">🎉 {record.holiday_name}</span>;
                    } else if (record.leave_type) {
                      remarks = (
                        <span className="text-amber-700 font-medium text-xs">
                          📋 {record.leave_type}
                          {record.leave_status === "pending" && (
                            <span className="ml-1 text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full">
                              Pending
                            </span>
                          )}
                        </span>
                      );
                    } else if (lateInfo && record.status === "Present") {
                      remarks = <span className="text-amber-600 font-mono text-xs">⏰ {lateInfo}</span>;
                    }

                    return (
                      <tr
                        key={record.attendance_id ?? `${record.attendance_date}-${idx}`}
                        className={`border-t border-slate-100 transition-colors hover:bg-indigo-50/30 ${cfg.rowBg}`}
                      >
                        <td className="py-3 px-3 whitespace-nowrap">
                          <div className="flex flex-col">
                            <span className="font-semibold text-slate-800 text-xs">{formatDate(record.attendance_date)}</span>
                            <span className="text-[10px] text-slate-400">{record.day_of_week ?? getDayName(record.attendance_date)}</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          {record.check_in
                            ? <span className="font-mono text-xs text-slate-700">{formatTime(record.check_in)}</span>
                            : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-3 px-3" style={{ maxWidth: "120px" }}>
                          <span className="block truncate text-xs text-slate-500">{record.check_in_location_name || "—"}</span>
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          {record.check_out
                            ? <span className="font-mono text-xs text-slate-700">{formatTime(record.check_out)}</span>
                            : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-3 px-3" style={{ maxWidth: "120px" }}>
                          <span className="block truncate text-xs text-slate-500">{record.check_out_location_name || "—"}</span>
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          {record.work_hours
                            ? <span className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-lg">{parseFloat(record.work_hours).toFixed(1)}h</span>
                            : record.check_in && record.check_out
                              ? <span className="font-mono text-xs text-slate-600">{calculateDuration(record.check_in, record.check_out)}</span>
                              : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.badge}`}>
                            <Icon className="w-3 h-3 flex-shrink-0" />
                            {record.status}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-xs whitespace-nowrap">
                          {remarks ?? <span className="text-slate-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 border-t border-indigo-100/60 flex justify-between items-center bg-indigo-50/30">
              <span className="text-xs text-slate-400">{filteredData.length} record{filteredData.length !== 1 ? "s" : ""}</span>
              <span className="text-xs text-slate-400">
                {new Date(selYear, selMonth - 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AttendanceTable;